import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects'
import { ReturnCall } from '../types'
import { actions as appActions } from '../state'
import { PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { actions } from './TransfersState'
import apis from './TransfersApi'
import {
  DownloadTransferListPagesInvoicesPayload,
  DownloadTransfersListsInvoicesPayload,
  GetTransfersListsPayload,
  IssueTransferListPagesInvoicesPayload,
  IssueTransfersListsInvoicesPayload,
  TableErrorsLog,
  RemoveTransferListPagePayload,
  RemoveTransferListPayload,
  TransferPageCageIssueOrDownloadPayload,
  TransferReceiveValidateBadgePayload,
  ReceiveStartPayload,
  ValidateProductBarcodePayload,
  ValidateMovementAddressPayload,
  ReceiveBatchesMovementPayload,
  ReceiveUnfitMovementPayload,
  ReceiveItem,
  TransferCageStatusEnum,
  GetReceiveStatusPayload,
  ChangeCageFcPayload,
  ReceiveValidateSupervisorBadgePayload,
  ReceiveSupervisorCountBatchesPayload,
  ReceiveSupervisorProductNotFoundPayload,
  SearchTransferCagesByFiltersPayload,
} from './TransfersTypes'

function* getSearchFilters() {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getSearchFilters> = yield call(apis.getSearchFilters)

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar os filtros de busca')
      return
    }

    yield put(actions.setProperty({ property: 'filtersSearchLists', value: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar os filtros de busca!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getTransfersLists(
  data: PayloadAction<GetTransfersListsPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getTransfersLists> = yield call(
      apis.getTransfersLists,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar as listas de transferências')
      return
    }

    yield put(actions.setProperty({ property: 'lastGetTransfersListsPayload', value: data.payload }))
    yield put(actions.setProperty({ property: 'tableTransfersListsPagination', value: result.data.pagination }))
    yield put(actions.setProperty({ property: 'transfersLists', value: result.data.transfers }))
  } catch (err: any) {
    toast.error('Falha ao buscar as listas de transferências!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* removeTransferList(data: PayloadAction<RemoveTransferListPayload>) {
  yield put(appActions.isLoading(true))

  const { idList, onError, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.removeTransferList> = yield call(
      apis.removeTransferList,
      idList,
    )

    if (!result.isOk) {
      yield put(appActions.isLoading(false))
      toast.error(result?.data?.data?.message || 'Falha ao excluir ou cancelar lista de transferência')
      onError()
      return
    }

    yield put(appActions.isLoading(false))
    onSuccess()
  } catch (err: any) {
    yield put(appActions.isLoading(false))
    toast.error('Falha ao excluir ou cancelar lista de transferência!')
    onError()
  }
}

function* downloadTransfersListsInvoices(data: PayloadAction<DownloadTransfersListsInvoicesPayload>) {
  yield put(appActions.isLoading(true))

  const { idsLists, onError, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.downloadTransfersListsInvoices> = yield call(
      apis.downloadTransfersListsInvoices,
      idsLists,
    )

    if (!result.isOk) {
      toast.error(result?.data?.message || 'Falha ao baixar notas das listas de transferência')
      onError()
      return
    }

    result.data.fileDownload()

    onSuccess(result.data.errors)
  } catch (err: any) {
    toast.error('Falha ao baixar notas das listas de transferência!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* issueTransfersListsInvoices(data: PayloadAction<IssueTransfersListsInvoicesPayload>) {
  yield put(appActions.isLoading(true))

  const { idsLists, onError, onSuccess } = data.payload

  try {
    const errors: TableErrorsLog[] = []

    for (const idList of idsLists) {
      const result: ReturnCall<typeof apis.issueTransferListInvoices> = yield call(
        apis.issueTransferListInvoices,
        idList,
      )

      if (!result.isOk) {
        errors.push({
          id: idList,
          message: result?.data?.data?.message || 'Erro desconhecido ao emitir notas',
        })
      } else if (result.data?.errors?.length > 0) {
        errors.push(...result.data.errors.map((error: string) => ({
          id: idList,
          message: error,
        })))
      }
    }

    onSuccess(errors)
  } catch (err: any) {
    toast.error('Falha ao emitir notas das listas de transferência!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getTransferListPages(data: PayloadAction<number>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getTransferListPages> = yield call(
      apis.getTransferListPages,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar páginas da lista de transferência')
      return
    }

    yield put(actions.setProperty({ property: 'transferListPages', value: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar páginas da lista de transferência!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* removeTransferListPage(data: PayloadAction<RemoveTransferListPagePayload>) {
  yield put(appActions.isLoading(true))

  const { idPage, onError, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.removeTransferListPage> = yield call(
      apis.removeTransferListPage,
      idPage,
    )

    if (!result.isOk) {
      yield put(appActions.isLoading(false))
      toast.error(result?.data?.data?.message || 'Falha ao excluir ou cancelar página da lista de transferência')
      onError()
      return
    }

    yield put(appActions.isLoading(false))
    onSuccess()
  } catch (err: any) {
    yield put(appActions.isLoading(false))
    toast.error('Falha ao excluir ou cancelar página da lista de transferência!')
    onError()
  }
}

function* downloadTransferListPagesInvoices(data: PayloadAction<DownloadTransferListPagesInvoicesPayload>) {
  yield put(appActions.isLoading(true))

  const { onError, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.downloadTransferListPagesInvoices> = yield call(
      apis.downloadTransferListPagesInvoices,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.message || 'Falha ao baixar notas das páginas da lista de transferência')
      onError()
      return
    }

    result.data.fileDownload()

    onSuccess(result.data.errors)
  } catch (err: any) {
    toast.error('Falha ao baixar notas das páginas da lista de transferência!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* issueTransferListPagesInvoices(data: PayloadAction<IssueTransferListPagesInvoicesPayload>) {
  yield put(appActions.isLoading(true))

  const { idsPages, onError, onSuccess } = data.payload

  try {
    const errors: TableErrorsLog[] = []

    for (const idPage of idsPages) {
      const result: ReturnCall<typeof apis.issueTransferListPageInvoices> = yield call(
        apis.issueTransferListPageInvoices,
        idPage,
      )

      if (!result.isOk) {
        errors.push({
          id: idPage,
          message: result?.data?.data?.message || 'Erro desconhecido ao emitir notas',
        })
      } else if (result.data?.errors?.length > 0) {
        errors.push(...result.data.errors.map((error: string) => ({
          id: idPage,
          message: error,
        })))
      }
    }

    onSuccess(errors)
  } catch (err: any) {
    toast.error('Falha ao emitir notas das páginas da lista de transferência!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getTransferPageDetails(data: PayloadAction<number>) {
  yield put(appActions.isLoading(true))
  try {
    actions.clearProperty('transferPageDetails')

    const result: ReturnCall<typeof apis.getTransferPageDetails> = yield call(
      apis.getTransferPageDetails,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar detalhes da página!')
      return
    }

    yield put(actions.setProperty({ property: 'transferPageDetails', value: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar detalhes da página!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* issueTransferListPageCageInvoice(data: PayloadAction<TransferPageCageIssueOrDownloadPayload>) {
  yield put(appActions.isLoading(true))

  const { idCage, setPreventDoubleClick } = data.payload

  try {
    const result: ReturnCall<typeof apis.issueTransferListPageCageInvoice> = yield call(
      apis.issueTransferListPageCageInvoice,
      idCage,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Erro desconhecido ao emitir nota')
      if (result.data?.data?.cage) {
        yield put(actions.updateCageDetails(result.data.data.cage))
      }
      return
    }

    yield put(actions.updateCageDetails(result.data))
  } catch (err: any) {
    toast.error('Falha ao emitir nota da gaiola!')
  } finally {
    yield put(appActions.isLoading(false))
    setPreventDoubleClick(false)
  }
}

function* downloadTransferListPageCageInvoice(data: PayloadAction<TransferPageCageIssueOrDownloadPayload>) {
  yield put(appActions.isLoading(true))

  const { idCage, setPreventDoubleClick } = data.payload

  try {
    const result: ReturnCall<typeof apis.downloadTransferListPageCageInvoice> = yield call(
      apis.downloadTransferListPageCageInvoice,
      idCage,
    )

    if (!result.isOk) {
      toast.error(result?.data?.message || 'Falha ao baixar nota de gaiola')
      return
    }

    result.data()
  } catch (err: any) {
    toast.error('Falha ao baixar nota de gaiola!')
  } finally {
    yield put(appActions.isLoading(false))
    setPreventDoubleClick(false)
  }
}

function* receiveValidateBadge(data: PayloadAction<TransferReceiveValidateBadgePayload>) {
  yield put(appActions.isLoading(true))

  const { badge, history, onError } = data.payload

  try {
    const result: ReturnCall<typeof apis.receiveValidateBadge> = yield call(
      apis.receiveValidateBadge,
      badge,
    )

    if (!result.isOk) {
      onError(result.data?.data?.message || 'Falha ao validar o crachá')
      return
    }

    yield put(actions.setProperty({ property: 'receiveUser', value: result.data }))

    history.push('/transfers/receive/start')
  } catch (err: any) {
    onError('Falha ao validar o crachá!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* receiveStart(data: PayloadAction<ReceiveStartPayload>) {
  yield put(appActions.isLoading(true))

  const { history, onFinish, inWaitingPage } = data.payload

  try {
    const result: ReturnCall<typeof apis.receiveStart> = yield call(
      apis.receiveStart,
      data.payload,
    )

    if (!result.isOk) {
      if (result?.data?.data?.cageStatus) {
        yield put(actions.updateReceiveDataStatus({
          idCage: result.data.data.cageStatus.idCage,
          idStatus: result.data.data.cageStatus.idStatus,
          status: result.data.data.cageStatus.status,
        }))
      }
      toast.error(result?.data?.data?.message || 'Falha ao iniciar recebimento de transferẽncia')
      return
    }

    const processingStatus = [
      TransferCageStatusEnum.PROCESSING_RESERVE_CONFIRMATION,
      TransferCageStatusEnum.PROCESSING_MAIN_STOCK_ENTRY,
      TransferCageStatusEnum.PROCESSING_PURCHASE_INTEGRATION,
      TransferCageStatusEnum.PROCESSING_FINANCES_INTEGRATION,
      TransferCageStatusEnum.PROCESSING_OPERATIONAL_STOCK_ENTRY,
    ]

    if (result.data.idStatus === TransferCageStatusEnum.FINISHED) {
      toast.info('Recebimento de transferência está finalizado!')
      yield put(actions.clearState())
      return
    }

    if (processingStatus.includes(result.data.idStatus)) {
      yield put(actions.setProperty({
        property: 'receiveData',
        value: {
          idCage: result.data.idCage,
          cageAddress: result.data.cageAddress,
          invoiceAccessKey: result.data.invoiceAccessKey,
          idStatus: result.data.idStatus,
          status: result.data.status,
          items: [],
        },
      }))

      if (!inWaitingPage) {
        history.push('/transfers/receive/waiting')
      }
      return
    }

    if (result.data.idStatus === TransferCageStatusEnum.IN_RESTOCKING) {
      if (result.data.items.length === 0) {
        toast.info('Não há itens para receber nesta transferência.')
        yield put(actions.clearState())
        return
      }

      const sortedItems = result.data.items.sort((a, b) => a.order - b.order)

      yield put(actions.setProperty({
        property: 'receiveData',
        value: {
          idCage: result.data.idCage,
          cageAddress: result.data.cageAddress,
          invoiceAccessKey: result.data.invoiceAccessKey,
          idStatus: result.data.idStatus,
          status: result.data.status,
          items: sortedItems,
          currentItem: sortedItems[0],
        },
      }))

      history.push('/transfers/receive/item')
      return
    }
  } catch (err: any) {
    toast.error('Falha ao iniciar recebimento de transferẽncia!')
  } finally {
    yield put(appActions.isLoading(false))
    onFinish()
  }
}

function* getReceiveStatus(data: PayloadAction<GetReceiveStatusPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getReceiveStatus> = yield call(
      apis.getReceiveStatus,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar status de recebimento de transferência')
      return
    }

    yield put(actions.updateReceiveDataStatus(result.data))
  } catch (err: any) {
    toast.error('Falha ao buscar status de recebimento de transferência!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* validateProductBarcode(data: PayloadAction<ValidateProductBarcodePayload>) {
  yield put(appActions.isLoading(true))

  const { onError, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.validateProductBarcode> = yield call(
      apis.validateProductBarcode,
      data.payload,
    )

    if (!result.isOk) {
      onError(result.data?.data?.message || 'Falha ao validar o código de barras do produto')
      return
    }

    onSuccess()
  } catch (err: any) {
    onError('Falha ao validar o código de barras do produto!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* validateMovementAddress(data: PayloadAction<ValidateMovementAddressPayload>) {
  yield put(appActions.isLoading(true))

  const { onError, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.validateMovementAddress> = yield call(
      apis.validateMovementAddress,
      data.payload,
    )

    if (!result.isOk) {
      onError(result.data?.data?.message || 'Endereço inválido')
      return
    }

    onSuccess()
  } catch (err: any) {
    onError('Falha ao validar endereço!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* receiveValidateSupervisorBadge(data: PayloadAction<ReceiveValidateSupervisorBadgePayload>) {
  yield put(appActions.isLoading(true))

  const { badge, onError, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.receiveValidateSupervisorBadge> = yield call(
      apis.receiveValidateSupervisorBadge,
      badge,
    )

    if (!result.isOk) {
      onError(result.data?.data?.message || 'Falha ao validar o crachá do fiscal')
      return
    }

    const { name } = result.data

    yield put(actions.setProperty({ property: 'receiveSupervisor', value: { badge, name } }))

    onSuccess()
  } catch (err: any) {
    onError('Falha ao validar o crachá do fiscal!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* receiveSupervisorProductNotFound(data: PayloadAction<ReceiveSupervisorProductNotFoundPayload>) {
  yield put(appActions.isLoading(true))

  const { item, onSuccess, onError } = data.payload

  try {
    const result: ReturnCall<typeof apis.receiveSupervisorProductNotFound> = yield call(
      apis.receiveSupervisorProductNotFound,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result.data.message || result.data?.data?.message || 'Falha ao registrar produto como não encontrado')
      onError()
      return
    }

    yield put(actions.setCurrentItemAsDone(item))

    if (result.data.finished) {
      toast.info('Recebimento de transferência finalizado com sucesso!')
      yield put(actions.clearState())
    } else {
      const items = (yield select((state) => state.transfers.receiveData.items)) as ReceiveItem[]

      const nextItem: ReceiveItem | undefined = items.find((i) => !i.done)

      yield put(actions.setCurrentReceiveItem(nextItem))

      if (nextItem) {
        onSuccess()
      } else {
        toast.info('Recebimento de transferência finalizado com sucesso!')
        yield put(actions.clearState())
      }
    }
  } catch (err: any) {
    toast.error('Falha ao registrar produto como não encontrado!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* receiveSupervisorCountBatches(data: PayloadAction<ReceiveSupervisorCountBatchesPayload>) {
  yield put(appActions.isLoading(true))

  const {
    idModel, idLocation, onSuccess, setPreventDoubleClick,
  } = data.payload

  try {
    const result: ReturnCall<typeof apis.receiveSupervisorCountBatches> = yield call(
      apis.receiveSupervisorCountBatches,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result.data?.data?.message || 'Falha ao registrar contagem e ajuste dos lotes')
      return
    }

    const sortedItems = result.data.items.sort((a, b) => a.order - b.order)

    yield put(actions.setProperty({
      property: 'receiveData',
      value: {
        idCage: result.data.idCage,
        cageAddress: result.data.cageAddress,
        invoiceAccessKey: result.data.invoiceAccessKey,
        idStatus: result.data.idStatus,
        status: result.data.status,
        items: sortedItems,
        currentItem: sortedItems[0],
      },
    }))

    yield put(actions.setProperty({ property: 'receiveSupervisor', value: undefined }))

    const goToNextStep = sortedItems[0].idModel === idModel && sortedItems[0].bestPosition.id === idLocation

    onSuccess(goToNextStep)
  } catch (err: any) {
    toast.error('Falha ao registrar contagem e ajuste dos lotes!')
  } finally {
    yield put(appActions.isLoading(false))
    setPreventDoubleClick(false)
  }
}

function* receiveMovement(data: PayloadAction<ReceiveBatchesMovementPayload>) {
  yield put(appActions.isLoading(true))

  const { item, onSuccess, onError } = data.payload

  try {
    const result: ReturnCall<typeof apis.receiveMovement> = yield call(
      apis.receiveMovement,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result.data.message || result.data?.data?.message || 'Falha ao realizar movimentação coube')
      return
    }

    yield put(actions.setCurrentItemAsDone(item))

    if (result.data.finished) {
      toast.info('Recebimento de transferência finalizado com sucesso!')
      yield put(actions.clearState())
    } else {
      const items = (yield select((state) => state.transfers.receiveData.items)) as ReceiveItem[]

      const nextItem: ReceiveItem | undefined = items.find((i) => !i.done)

      yield put(actions.setCurrentReceiveItem(nextItem))

      if (nextItem) {
        onSuccess()
      } else {
        toast.info('Recebimento de transferência finalizado com sucesso!')
        yield put(actions.clearState())
      }
    }
  } catch (err: any) {
    toast.error('Falha ao realizar movimentação coube!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* receiveUnfitMovement(data: PayloadAction<ReceiveUnfitMovementPayload>) {
  yield put(appActions.isLoading(true))

  const {
    badge, item, startedAt, onSuccess, onError,
  } = data.payload

  try {
    const items = (yield select((state) => state.transfers.receiveData.items)) as ReceiveItem[]

    const result: ReturnCall<typeof apis.receiveUnfitMovement> = yield call(
      apis.receiveUnfitMovement,
      {
        badge,
        item,
        items,
        startedAt,
      },
    )

    if (!result.isOk) {
      toast.error(result.data?.data?.message || 'Falha ao realizar movimentação não coube')
      onError()
      return
    }

    const sortedItems = result.data.sort((a, b) => a.order - b.order)

    yield put(actions.updateReceiveDataItems(sortedItems))
    yield put(actions.setCurrentReceiveItem(sortedItems[0]))

    onSuccess()
  } catch (err: any) {
    toast.error('Falha ao realizar movimentação não coube!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getCageAddressInfo(data: PayloadAction<string>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getCageAddressInfo> = yield call(
      apis.getCageAddressInfo,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result.data?.data?.message || 'Falha ao obter informações da gaiola')
      return
    }

    yield put(actions.setProperty({ property: 'cageAddressInfo', value: result.data }))

    toast.success('Informações da gaiola obtidas com sucesso!')
  } catch (err: any) {
    toast.error('Falha ao obter informações da gaiola!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* changeCageFc(data: PayloadAction<ChangeCageFcPayload>) {
  yield put(appActions.isLoading(true))

  const { idLocation, idFc, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.changeCageFc> = yield call(
      apis.changeCageFc,
      idLocation,
      idFc,
    )

    if (!result.isOk) {
      toast.error(result.data?.data?.message || 'Falha ao alterar o FC da gaiola')
      return
    }

    onSuccess()
  } catch (err: any) {
    toast.error('Falha ao alterar o FC da gaiola!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* searchCagesByFilters(data: PayloadAction<SearchTransferCagesByFiltersPayload>) {
  yield put(appActions.isLoading(true))

  const { onFinally } = data.payload

  try {
    const result: ReturnCall<typeof apis.searchCagesByFilters> = yield call(
      apis.searchCagesByFilters,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar gaiolas de transferência')
      return
    }

    yield put(actions.setProperty({ property: 'transferCages', value: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar gaiolas de transferência!')
  } finally {
    yield put(appActions.isLoading(false))
    onFinally()
  }
}

function* getCageDetails(data: PayloadAction<number>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getCageDetails> = yield call(
      apis.getCageDetails,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar detalhes da gaiola de transferência')
      return
    }

    yield put(actions.setProperty({ property: 'transferCageDetails', value: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar detalhes da gaiola de transferência!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getSearchFilters.type, getSearchFilters),
    takeLatest(actions.getTransfersLists.type, getTransfersLists),
    takeLatest(actions.removeTransferList.type, removeTransferList),
    takeLatest(actions.downloadTransfersListsInvoices.type, downloadTransfersListsInvoices),
    takeLatest(actions.issueTransfersListsInvoices.type, issueTransfersListsInvoices),
    takeLatest(actions.getTransferListPages.type, getTransferListPages),
    takeLatest(actions.removeTransferListPage.type, removeTransferListPage),
    takeLatest(actions.downloadTransferListPagesInvoices.type, downloadTransferListPagesInvoices),
    takeLatest(actions.issueTransferListPagesInvoices.type, issueTransferListPagesInvoices),
    takeLatest(actions.getTranferPageDetails.type, getTransferPageDetails),
    takeLatest(actions.issueTransferListPageCageInvoice.type, issueTransferListPageCageInvoice),
    takeLatest(actions.downloadTransferListPageCageInvoice.type, downloadTransferListPageCageInvoice),
    takeLatest(actions.receiveValidateBadge.type, receiveValidateBadge),
    takeLatest(actions.receiveStart.type, receiveStart),
    takeLatest(actions.getReceiveStatus.type, getReceiveStatus),
    takeLatest(actions.validateProductBarcode.type, validateProductBarcode),
    takeLatest(actions.validateMovementAddress.type, validateMovementAddress),
    takeLatest(actions.receiveValidateSupervisorBadge.type, receiveValidateSupervisorBadge),
    takeLatest(actions.receiveSupervisorProductNotFound.type, receiveSupervisorProductNotFound),
    takeLatest(actions.receiveSupervisorCountBatches.type, receiveSupervisorCountBatches),
    takeLatest(actions.receiveMovement.type, receiveMovement),
    takeLatest(actions.receiveUnfitMovement.type, receiveUnfitMovement),
    takeLatest(actions.getCageAddressInfo.type, getCageAddressInfo),
    takeLatest(actions.changeCageFc.type, changeCageFc),
    takeLatest(actions.searchCagesByFilters.type, searchCagesByFilters),
    takeLatest(actions.getCageDetails.type, getCageDetails),
  ])
}
